import {
  BlockoutRule,
  BlockoutType,
  CapacityRule,
  DaySchedule,
  SpecialOpening,
  Venue,
  VenueSchedule
} from '@/types';
import { normalizeSlotIntervalMinutes } from '@/lib/scheduling';

export const DEFAULT_SCHEDULE: VenueSchedule = {
  weekly: {
    0: { open: false, start: '09:00', end: '17:00', capacity: 1 },
    1: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    2: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    3: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    4: { open: true, start: '09:00', end: '17:00', capacity: 1 },
    5: { open: true, start: '09:00', end: '16:00', capacity: 1 },
    6: { open: false, start: '09:00', end: '17:00', capacity: 1 }
  },
  blockouts: [],
  slotIntervalMinutes: 30,
  capacityRules: [],
  specialOpenings: []
};

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export type VenueRuleFrequency = NonNullable<BlockoutRule['frequency']>;
export type DayScheduleField = Extract<keyof DaySchedule, 'open' | 'start' | 'end' | 'capacity'>;
export type DayScheduleValue = boolean | string | number;

export function isVenueRuleFrequency(value: string): value is VenueRuleFrequency {
  return value === 'every' || value === 'biweekly';
}

export function normalizeDayCapacity(value?: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(50, Math.max(0, Math.floor(parsed)));
}

export function timeToMinutes(value: string): number | null {
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

export function rangesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  const startAMin = timeToMinutes(startA);
  const endAMin = timeToMinutes(endA);
  const startBMin = timeToMinutes(startB);
  const endBMin = timeToMinutes(endB);
  if (startAMin == null || endAMin == null || startBMin == null || endBMin == null) return false;
  return startAMin < endBMin && endAMin > startBMin;
}

export function parseCoordinatesDraft(draft: { lat: string; lng: string }) {
  const latRaw = String(draft.lat || '').trim();
  const lngRaw = String(draft.lng || '').trim();
  const hasValue = Boolean(latRaw || lngRaw);
  if (!hasValue) return { coords: null, hasValue: false, isValid: true };
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  const withinRange = lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !withinRange) {
    return { coords: null, hasValue: true, isValid: false };
  }
  return { coords: { lat, lng }, hasValue: true, isValid: true };
}

export function getScheduleWithDefaults(schedule?: VenueSchedule): VenueSchedule {
  const weekly = { ...DEFAULT_SCHEDULE.weekly, ...(schedule?.weekly || {}) };
  const normalizedWeekly = Object.keys(DEFAULT_SCHEDULE.weekly).reduce(
    (acc, key) => {
      const dayKey = Number(key);
      const day = weekly[dayKey] || DEFAULT_SCHEDULE.weekly[dayKey];
      acc[dayKey] = {
        ...day,
        capacity: normalizeDayCapacity(
          day.capacity ?? DEFAULT_SCHEDULE.weekly[dayKey].capacity ?? 1
        )
      };
      return acc;
    },
    {} as Record<number, DaySchedule>
  );
  return {
    ...DEFAULT_SCHEDULE,
    ...schedule,
    weekly: normalizedWeekly,
    blockouts: [...(schedule?.blockouts || [])],
    capacityRules: [...(schedule?.capacityRules || [])],
    specialOpenings: [...(schedule?.specialOpenings || [])],
    slotIntervalMinutes: normalizeSlotIntervalMinutes(
      schedule?.slotIntervalMinutes ?? DEFAULT_SCHEDULE.slotIntervalMinutes
    )
  };
}

type ConflictDirection = 'addingBlockout' | 'addingSpecialOpening';

export function findBlockoutSpecialOpeningConflict(
  schedule: VenueSchedule,
  candidate:
    | { kind: 'blockout'; rule: BlockoutRule }
    | { kind: 'specialOpening'; opening: SpecialOpening }
): { date: string; reason: string } | null {
  const blockouts = schedule.blockouts || [];
  const openings = schedule.specialOpenings || [];

  if (candidate.kind === 'blockout') {
    const { rule } = candidate;
    if (rule.type === 'single' && rule.date) {
      const match = openings.find((o) => o.date === rule.date);
      if (match) return { date: rule.date, reason: rule.reason || 'Closed' };
    }
    if (rule.type === 'range' && rule.startDate && rule.endDate) {
      const match = openings.find((o) => o.date >= rule.startDate! && o.date <= rule.endDate!);
      if (match) return { date: match.date, reason: rule.reason || 'Closed' };
    }
    return null;
  }

  const { opening } = candidate;
  for (const rule of blockouts) {
    if (rule.type === 'single' && rule.date === opening.date) {
      return { date: opening.date, reason: rule.reason || 'Closed' };
    }
    if (
      rule.type === 'range' &&
      rule.startDate &&
      rule.endDate &&
      opening.date >= rule.startDate &&
      opening.date <= rule.endDate
    ) {
      return { date: opening.date, reason: rule.reason || 'Closed' };
    }
  }
  return null;
}

export function buildConflictMessage(
  direction: ConflictDirection,
  conflict: { date: string; reason: string }
): string {
  const action =
    direction === 'addingBlockout'
      ? `This blockout overlaps a special opening already configured for ${conflict.date}.`
      : `This special opening overlaps an existing blockout on ${conflict.date} ("${conflict.reason}").`;
  return `${action} Special openings override blockouts, so the venue will appear bookable online that day. Continue anyway?`;
}

export function makeNewVenue(): Venue {
  return { id: '', name: '', address: '', city: '', image: '', schedule: DEFAULT_SCHEDULE };
}
